/**
 * @file AnimatedSection.tsx
 * @description Reusable animated section component for consistent animations
 *
 * @cursorRules
 * - animation-guidelines.mdc - Animation principles
 * - modern-component-patterns.mdc - Next.js component patterns
 */
"use client"

import { ReactNode } from "react"
import {
  motion,
  MotionProps,
  TargetAndTransition,
  Transition,
} from "framer-motion"

// Create motion components
const MotionSection = motion.create("section")

interface AnimatedSectionProps
  extends Omit<MotionProps, "animate" | "initial" | "whileInView"> {
  children: ReactNode
  className?: string
  id?: string
  /** Use 'animate' for Hero (immediate animation), 'whileInView' for scroll-triggered sections */
  animationType?: "animate" | "whileInView"
  /** Optional custom animation values */
  customAnimation?: {
    initial?: TargetAndTransition
    animate?: TargetAndTransition
    transition?: Record<string, unknown>
  }
  /** Whether to repeat animation on scroll (default false) */
  repeatOnScroll?: boolean
}

export const AnimatedSection = ({
  children,
  className = "",
  id,
  animationType = "whileInView",
  customAnimation,
  repeatOnScroll = false,
  ...motionProps
}: AnimatedSectionProps) => {
  // Default animation values - more dramatic
  const defaultAnimation = {
    initial: { opacity: 0, y: 50 } as TargetAndTransition,
    animate: { opacity: 1, y: 0 } as TargetAndTransition,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }

  // Merge custom animations with defaults
  const animation = {
    initial: customAnimation?.initial || defaultAnimation.initial,
    animate: customAnimation?.animate || defaultAnimation.animate,
    transition: customAnimation?.transition || defaultAnimation.transition,
  }

  // Determine which animation prop to use
  const animationProps =
    animationType === "animate"
      ? {
          initial: animation.initial as TargetAndTransition,
          animate: animation.animate as TargetAndTransition,
        }
      : {
          initial: animation.initial as TargetAndTransition,
          whileInView: animation.animate as TargetAndTransition,
        }

  return (
    <MotionSection
      id={id}
      className={className}
      viewport={{ once: !repeatOnScroll, amount: 0.3 }}
      transition={animation.transition as Transition}
      {...animationProps}
      {...motionProps}
    >
      {children}
    </MotionSection>
  )
}
